/**
 * Hook de permissões do sistema.
 * Usa as permissões do usuário logado (salvas no localStorage).
 */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 * MÓDULOS DESLIGADOS — pedido do dono, 2026-09-08: *"remover menu de assistente IA, remover
 * menu CHAT, Mobilização de Obra, BOM/Materiais e equipes terceiras (DESATIVAR APENAS)"*.
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 *
 * DESATIVAR, E NÃO APAGAR — foi o que ele pediu, e é a diferença que importa: as telas, as
 * rotas do servidor, os testes e principalmente **os dados** continuam todos lá. O que muda é
 * que ninguém chega neles pela interface. Religar é apagar uma linha desta lista.
 *
 * POR QUE AQUI, E NÃO NO MENU. Esconder o item do menu resolveria só o menu — a rota continuaria
 * respondendo por link direto, favorito ou histórico do navegador, e qualquer botão de outra
 * tela continuaria levando lá. `canAccess` é a pergunta que o menu, o guarda de rota
 * (`ROUTE_MODULE_MAP`) e as telas fazem ANTES de mostrar qualquer coisa: desligando aqui, as
 * três portas fecham juntas e nenhuma pode ser esquecida depois. É a regra 5 do `CLAUDE.md`
 * aplicada de véspera — em vez de fechar uma porta e descobrir as laterais pelo relato.
 *
 * ⚠️ NÃO É PERMISSÃO. Permissão é por PESSOA e o dono edita na tela de Usuários; isto vale para
 * TODO MUNDO, master inclusive, e por isso mora no código e não no banco. Um módulo desligado
 * aqui não volta dando permissão a ninguém — e é isso que se quer de um recurso que a empresa
 * decidiu não usar.
 */
export const MODULOS_DESLIGADOS = [
  'assistente_ia',        // Assistente IA
  'chat',                 // Chat (o menu E a bolha flutuante — ver `ChatWidget`)
  'checklist_mobilizacao',// Mobilização de Obra
  'bom',                  // BOM / Materiais  (≠ `materiais`, que é a Solicitação de Material)
  'terceiros',            // Equipes Terceiras
];

/**
 * Retorna as permissões do usuário logado.
 */
export const getUserPermissions = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.permissoes || {};
  } catch {
    return {};
  }
};

/**
 * Hook para verificar permissão em um módulo específico.
 * @param {string} module - ID do módulo (ex: "dashboard", "clientes")
 * @returns {{ canView: boolean, canEdit: boolean, level: string }}
 */
export const usePermission = (module) => {
  const perms = getUserPermissions();
  // Modulo desligado nao tem nivel — nem para ver, nem para editar. Ver `MODULOS_DESLIGADOS`.
  const level = MODULOS_DESLIGADOS.includes(module) ? 'nenhum' : (perms[module] || 'nenhum');
  return {
    canView: level === 'visualizar' || level === 'editar',
    canEdit: level === 'editar',
    level,
  };
};

/**
 * Verifica se o usuário pode acessar um módulo.
 */
export const canAccess = (module) => {
  if (MODULOS_DESLIGADOS.includes(module)) return false;
  const perms = getUserPermissions();
  const level = perms[module] || 'nenhum';
  return level === 'visualizar' || level === 'editar';
};

/**
 * Verifica se o usuário pode editar um módulo.
 */
export const canEdit = (module) => {
  if (MODULOS_DESLIGADOS.includes(module)) return false;
  const perms = getUserPermissions();
  return (perms[module] || 'nenhum') === 'editar';
};

/**
 * "Admin e acima" — espelha `pode_gerir` do backend (deps.py):
 *   role in ("admin", "superadmin") or is_master_user(user)
 *
 * Existe porque `user.role === 'admin'` espalhado pelas telas ESCONDIA o botão do DONO: master
 * é role `superadmin` OU e-mail em MASTER_EMAILS, e nenhum dos dois é a string "admin". O
 * backend tinha o mesmo literal em 10 rotas e foi corrigido; sem isto aqui o conserto ficaria
 * inerte — a rota destranca e o botão continua invisível para quem mais precisa dele.
 *
 * `is_master` vem do /auth/me. Aceita o user por parâmetro (as telas já o têm em mão) ou lê do
 * localStorage.
 */
export const podeGerir = (user) => {
  try {
    const u = user || JSON.parse(localStorage.getItem('user') || '{}');
    return u?.is_master === true || u?.role === 'superadmin' || u?.role === 'admin';
  } catch { return false; }
};

export default usePermission;
